import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { EASE_IN_OUT, EASE_OUT, MATRIX_DARK, MATRIX_GRAY, MATRIX_GREEN, MATRIX_GREEN_DIM } from '../constants';

export function SystemModule({ nameKey, descriptionKey, delay = 0 }: { nameKey: string; descriptionKey: string; delay?: number }) {
  const { t } = useTranslation(['components/trial/lp/v2c']);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!inView) return undefined;
    const timer = setTimeout(() => setLoaded(true), delay * 1000 + 1200);
    return () => clearTimeout(timer);
  }, [inView, delay]);

  const name = t(nameKey);
  const fileName = `${name.toUpperCase().replace(/\s+/g, '_')}.dll`;

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }} transition={{ duration: 0.4, delay, ease: EASE_OUT }} className='rounded-lg border p-4 transition-all duration-500 md:p-5' style={{ backgroundColor: loaded ? `${MATRIX_GREEN}08` : `${MATRIX_DARK}`, borderColor: loaded ? `${MATRIX_GREEN}40` : `${MATRIX_GREEN}15`, boxShadow: loaded ? `0 0 20px ${MATRIX_GREEN}10` : 'none' }}>
      <div className='mb-2 font-mono text-xs md:text-sm' style={{ fontFamily: 'var(--font-jetbrains), monospace' }}>
        <span style={{ color: MATRIX_GREEN_DIM }}>[</span>
        <motion.span animate={inView ? { color: loaded ? MATRIX_GREEN : '#FFB800' } : { color: MATRIX_GREEN_DIM }} transition={{ duration: 0.3 }}>
          {loaded ? 'LOADED' : 'LOADING'}
        </motion.span>
        <span style={{ color: MATRIX_GREEN_DIM }}>]</span> <span style={{ color: MATRIX_GREEN }}>{fileName}</span>
        {loaded && (
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ color: MATRIX_GREEN }}>
            {' '}
            ... COMPLETE
          </motion.span>
        )}
        {!loaded && inView && (
          <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity }} style={{ color: '#FFB800' }}>
            {' '}
            ...
          </motion.span>
        )}
      </div>

      <h3 className='mb-1 text-base font-semibold md:text-lg' style={{ fontFamily: 'var(--font-bai)', color: loaded ? MATRIX_GREEN : '#FFFFFF' }}>
        {name}
      </h3>
      <p className='text-sm leading-relaxed' style={{ color: MATRIX_GRAY }}>
        {t(descriptionKey)}
      </p>

      {loaded && <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 0.8, ease: EASE_IN_OUT }} className='mt-3 h-px' style={{ backgroundColor: `${MATRIX_GREEN}40` }} />}
    </motion.div>
  );
}
